import React, { useState } from 'react';
import { FaCheckCircle, FaHandshake, FaTimes } from 'react-icons/fa';
import { motion } from 'framer-motion';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const benefits = [
  "The assurance of UK Qualification Regulation. FIC is respected by UK regulators as a credible and consistent awarding organisation.",
  "FIC qualifications are student loanable in the EU (subject to the applicant meeting the SLC criteria).",
  "Responsive and helpful customer service from dedicated representatives available 24/7.",
  "Learning programmes and qualifications that are current and relevant to today's global vocational requirements.",
  "Fast track pathways to university degrees at respected universities in the United Kingdom and the United States.",
  "Support for learning programme delivery from the education experts at FIC.",
  "Level 7 and Level 8 programmes for students desiring post-graduate qualifications.",
  "Marketing support.",
  "You will have access to our University partners enabling your organisation to enter or extend its reach into HE."
];

const Centers = () => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    title: '',
    learningCentre: '',
    email: '',
    telephone: '',
    address: '',
    city: '',
    country: '',
    website: '',
    numberOfStudents: '',
    currentQualifications: '',
    assistanceSummary: '',
    contactByEmail: false,
    contactByPhone: false
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
    // You can add API call or other logic here
  };
  return (
    <div className="max-w-full">
      {/* Hero Section with Background Image */}
      <div 
        className="relative h-96 w-full bg-cover bg-center"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1541339907198-e08756dedf3f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80')",
          backgroundPosition: 'center center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
      </div>
      
      {/* Moved Title Below Image */}
      <motion.div 
        className="text-center py-12 bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeInUp}
      >
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900">Flexible, Responsive and Innovative</h1>
      </motion.div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

      <motion.div 
        className="prose prose-lg max-w-4xl mx-auto mb-12 text-black"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <motion.p className="mb-6 text-black" variants={fadeInUp}>
          Learning Centres are on the front lines of developing critically-important skilled and productive workforces. 
          You are the ones who understand the priorities and the needs of the learners who walk through your doors.
        </motion.p>
        
        <motion.p className="mb-6 text-black" variants={fadeInUp}>
          FIC shares your commitment to helping motivated learners become the dynamic workforce of the future. 
          We work with our centres to assure you have the support and tools you need to achieve your important mission.
        </motion.p>
        
        <motion.p className="mb-8 text-black" variants={fadeInUp}>
          FIC will accept your learners and recognise prior learning (RPL) either from qualifications previously 
          taken or evaluate credit from their workplace.
        </motion.p>

        <motion.h2 
          className="text-2xl font-bold text-gray-900 mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          When you partner with FIC to become an Approved Learning Centre, you receive these benefits:
        </motion.h2>

        <motion.ul 
          className="space-y-4 mb-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          {benefits.map((benefit, index) => (
            <motion.li 
              key={index} 
              className="flex items-start"
              variants={fadeInUp}
            >
              <FaCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
              <span>{benefit}</span>
            </motion.li>
          ))}
        </motion.ul>

        <motion.div 
          className="bg-[#800000] p-6 rounded-lg mb-8 text-white"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <motion.h2 
            className="text-2xl font-bold mb-4 flex items-center text-white"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <FaHandshake className="text-white mr-3" />
            Centre Approval
          </motion.h2>
          <motion.p 
            className="mb-4 text-white"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            To offer any FIC qualification, you will need to obtain centre approval followed by qualification approval.
          </motion.p>
          <motion.p 
            className="mb-4 text-white"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            Although rigorous, our regulatory process follows a straightforward quality assurance route to deliver our qualifications. 
            We assess that you have the staff expertise, sufficient resources and premises to meet these requirements. 
            We have streamlined our timescale for centre approvals with the introduction of an initial online application process.
          </motion.p>
        </motion.div>

        <motion.div 
          className="bg-[#800000] p-6 rounded-lg mb-8 text-white !text-white"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <motion.h2 
            className="text-2xl font-bold mb-4 text-white"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            Approval Visit
          </motion.h2>
          <motion.p 
            className="mb-4 text-white"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            Once you apply for approval as a FIC Centre, you will be visited by one of our representatives. 
            This is a cooperative exercise that takes the form of an extended exchange of information. 
            The aim of the visit is to gather information and evidence of your organization's attitudes and 
            approach to the management of a FIC approved centre.
          </motion.p>
          <motion.p 
            className="text-white"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            Once you are an Approved FIC Centre, you can be assured of responsive service for you and your learners, 
            innovative learning solutions and the highest standards of quality and credibility.
          </motion.p>
        </motion.div>

        <motion.div 
          className="bg-[#800000] p-6 rounded-lg text-white !text-white"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              className="text-2xl font-bold mb-4 text-white"
              variants={fadeInUp}
            >
              Endorsed Awards
            </motion.h2>
            <motion.p 
              className="text-white"
              variants={fadeInUp}
            >
              In addition, FIC accredits programmes and courses provided by centres and organisations that are industry specific. 
              We have a wide range of skills sectors available and partner with other awarding organisations to provide flexible solutions. 
            </motion.p>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div 
        className="mt-12 text-center"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.5 }}
      >
        <motion.button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm !text-white bg-[#800000] hover:bg-[#6d0000] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#800000]"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Become An Approved Learning Center
        </motion.button>
      </motion.div>

      {/* Form Modal */}
      {showForm && (
        <motion.div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div 
            className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Become An Approved Learning Center</h2>
              <motion.button 
                onClick={() => setShowForm(false)}
                className="text-gray-500 hover:text-gray-700"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaTimes className="h-6 w-6" />
              </motion.button>
            </div>
            
            <motion.p 
              className="mb-6 text-gray-700"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              YES! I would like to learn more about becoming an Approved Qualifi Learning Centre.
            </motion.p>

            <motion.form 
              onSubmit={handleSubmit} 
              className="space-y-4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    required
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    required
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  required
                  value={formData.title}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="learningCentre" className="block text-sm font-medium text-gray-700">Learning Centre *</label>
                <input
                  type="text"
                  id="learningCentre"
                  name="learningCentre"
                  required
                  value={formData.learningCentre}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="telephone" className="block text-sm font-medium text-gray-700">Telephone *</label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    required
                    value={formData.telephone}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700">Address *</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  required
                  value={formData.address}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700">City *</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    required
                    value={formData.city}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700">Country *</label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    required
                    value={formData.country}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-700">Website</label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="numberOfStudents" className="block text-sm font-medium text-gray-700">Number of Students *</label>
                <input
                  type="number"
                  id="numberOfStudents"
                  name="numberOfStudents"
                  required
                  min="0"
                  value={formData.numberOfStudents}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="currentQualifications" className="block text-sm font-medium text-gray-700">What qualifications do you currently offer? *</label>
                <textarea
                  id="currentQualifications"
                  name="currentQualifications"
                  required
                  rows="3"
                  value={formData.currentQualifications}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="assistanceSummary" className="block text-sm font-medium text-gray-700">Please provide a summary of how we can assist you? *</label>
                <textarea
                  id="assistanceSummary"
                  name="assistanceSummary"
                  required
                  rows="3"
                  value={formData.assistanceSummary}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="pt-2">
                <p className="text-sm font-medium text-gray-700 mb-2">I am happy to be contacted by (please select as appropriate):</p>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      id="contactByEmail"
                      name="contactByEmail"
                      type="checkbox"
                      checked={formData.contactByEmail}
                      onChange={(e) => setFormData({...formData, contactByEmail: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="contactByEmail" className="ml-2 block text-sm text-gray-700">
                      Email
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="contactByPhone"
                      name="contactByPhone"
                      type="checkbox"
                      checked={formData.contactByPhone}
                      onChange={(e) => setFormData({...formData, contactByPhone: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="contactByPhone" className="ml-2 block text-sm text-gray-700">
                      Phone
                    </label>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#800000] hover:bg-[#6d0000] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#800000]"
                >
                  ENQIURE NOW
                </button>
              </div>
            </motion.form>
          </motion.div>
        </motion.div>
      )}
      </div>
    </div>
  );
};

export default Centers;
